import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardShell from '@/components/DashboardShell';
import { Button } from '@/components/ui/button';
import { 
  Plus, Clock, CheckCircle, XCircle, MapPin, Search,
  User, Star, Phone, MessageCircle, Navigation, Loader2, ChevronRight, Briefcase,
  Wrench, ArrowRight, AlertCircle, Shield
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const CustomerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [contractorOnboardingStatus, setContractorOnboardingStatus] = useState(null);

  useEffect(() => {
    fetchJobs();
    fetchContractorOnboardingStatus();
    setupWebSocket();
    
    // Listen for logout events to clean up WebSocket
    const handleLogoutEvent = () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
    
    window.addEventListener('auth:logout', handleLogoutEvent);
    
    return () => {
      window.removeEventListener('auth:logout', handleLogoutEvent);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const setupWebSocket = () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsUrl = `${WS_URL}/ws/${token}`;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('[WS] Customer message:', data);

      if (data.event === 'job_status_update') {
        handleStatusUpdate(data.data);
      } else if (data.event === 'contractor_assigned') {
        handleContractorAssigned(data.data);
      }
    };

    wsRef.current.onclose = () => {
      // Reconnect after 3 seconds
      setTimeout(setupWebSocket, 3000);
    };
  };

  const handleStatusUpdate = (data) => {
    setJobs(prev => prev.map(job => 
      job.id === data.job_id ? { ...job, status: data.status } : job
    ));
    
    const statusMessages = {
      'searching': 'Searching for contractors nearby...',
      'offered': 'Found a contractor! Waiting for response...',
      'accepted': 'Contractor accepted! They\'re on the way.',
      'en_route': 'Contractor is on the way!',
      'arrived': 'Contractor has arrived!',
      'in_progress': 'Work has started!',
      'completed': 'Job completed!',
      'expired': 'No contractors available. Please try again.',
    };
    
    if (statusMessages[data.status]) {
      toast.info(statusMessages[data.status]);
    }
    
    fetchJobs(); // Refresh to get latest data
  };

  const handleContractorAssigned = (data) => {
    toast.success(`${data.contractor.name} is on the way! ETA: ${data.contractor.eta_minutes} mins`);
    fetchJobs();
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/jobs`);
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchContractorOnboardingStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/contractor-onboarding/status`);
      if (response.data && response.data.onboarding_status !== 'none') {
        setContractorOnboardingStatus(response.data);
      }
    } catch (error) {
      // No onboarding status - that's fine
      console.log('No contractor onboarding status');
    }
  };

  const handleStartContractorOnboarding = () => {
    // Store current location as return URL
    sessionStorage.setItem('contractorOnboardingReturnTo', '/dashboard');
    navigate('/become-contractor');
  };

  const getStatusConfig = (status) => {
    const configs = {
      'requested': { 
        icon: Clock, 
        color: 'text-blue-500', 
        bg: 'bg-blue-500/10',
        text: 'Requested',
        description: 'Waiting for payment'
      },
      'searching': { 
        icon: Search, 
        color: 'text-yellow-500', 
        bg: 'bg-yellow-500/10',
        text: 'Searching',
        description: 'Finding contractors nearby...',
        animated: true
      },
      'offered': { 
        icon: User, 
        color: 'text-orange-500', 
        bg: 'bg-orange-500/10',
        text: 'Offered',
        description: 'Waiting for contractor response...',
        animated: true
      },
      'accepted': { 
        icon: CheckCircle, 
        color: 'text-green-500', 
        bg: 'bg-green-500/10',
        text: 'Accepted',
        description: 'Contractor assigned!'
      },
      'assigned': { 
        icon: CheckCircle, 
        color: 'text-green-500', 
        bg: 'bg-green-500/10',
        text: 'Assigned',
        description: 'Contractor assigned!'
      },
      'en_route': { 
        icon: Navigation, 
        color: 'text-blue-500', 
        bg: 'bg-blue-500/10',
        text: 'En Route',
        description: 'Contractor is on the way'
      },
      'arrived': { 
        icon: MapPin, 
        color: 'text-purple-500', 
        bg: 'bg-purple-500/10',
        text: 'Arrived',
        description: 'Contractor has arrived'
      },
      'in_progress': { 
        icon: Loader2, 
        color: 'text-blue-500', 
        bg: 'bg-blue-500/10',
        text: 'In Progress',
        description: 'Work in progress',
        animated: true
      },
      'completed': { 
        icon: CheckCircle, 
        color: 'text-green-500', 
        bg: 'bg-green-500/10',
        text: 'Completed',
        description: 'Job completed successfully'
      },
      'cancelled': { 
        icon: XCircle, 
        color: 'text-red-500', 
        bg: 'bg-red-500/10',
        text: 'Cancelled',
        description: 'Job was cancelled'
      },
      'expired': { 
        icon: XCircle, 
        color: 'text-orange-500', 
        bg: 'bg-orange-500/10',
        text: 'Expired',
        description: 'No contractors available'
      }
    };
    return configs[status] || configs['requested'];
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { 'GBP': '£', 'USD': '$', 'EUR': '€', 'AED': 'د.إ', 'INR': '₹' };
    return symbols[currency] || currency;
  };

  const canCancelJob = (job) => {
    const cancellableStatuses = ['requested', 'searching', 'offered'];
    if (!cancellableStatuses.includes(job.status)) return false;
    
    const createdAt = new Date(job.created_at);
    const now = new Date();
    const minutesSinceCreation = (now - createdAt) / (1000 * 60);
    return minutesSinceCreation <= 60;
  };

  const openCancelDialog = (job) => {
    setJobToCancel(job);
    setCancelDialogOpen(true);
  };

  const confirmCancelJob = async () => {
    if (!jobToCancel) return;
    
    setCancelling(true);
    try {
      await axios.post(`${API_URL}/jobs/${jobToCancel.id}/cancel`);
      toast.success('Job cancelled successfully');
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel job');
    } finally {
      setCancelling(false);
      setCancelDialogOpen(false);
      setJobToCancel(null);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      // Close WebSocket before logout
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      await logout();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Failed to log out');
    } finally {
      setLoggingOut(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Separate active and past jobs
  const activeJobs = jobs.filter(j => 
    !['completed', 'cancelled', 'expired'].includes(j.status)
  );
  const pastJobs = jobs.filter(j => 
    ['completed', 'cancelled', 'expired'].includes(j.status)
  );

  return (
    <DashboardShell>
      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Job?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this job? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Keep Job</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmCancelJob}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700"
            >
              {cancelling ? 'Cancelling...' : 'Yes, Cancel Job'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="space-y-8">
        {/* Contractor Onboarding Banner */}
        {contractorOnboardingStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-xl p-4 border ${
              contractorOnboardingStatus.onboarding_status === 'draft' 
                ? 'bg-blue-50 border-blue-200' 
                : contractorOnboardingStatus.onboarding_status === 'submitted'
                  ? 'bg-amber-50 border-amber-200'
                  : contractorOnboardingStatus.onboarding_status === 'rejected'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-green-50 border-green-200'
            }`}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {contractorOnboardingStatus.onboarding_status === 'draft' && (
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-blue-600" />
                  </div>
                )}
                {contractorOnboardingStatus.onboarding_status === 'submitted' && (
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                )}
                {contractorOnboardingStatus.onboarding_status === 'rejected' && (
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                )}
                {contractorOnboardingStatus.onboarding_status === 'approved' && (
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-green-600" />
                  </div>
                )}
                <div>
                  <p className={`font-medium ${
                    contractorOnboardingStatus.onboarding_status === 'draft' ? 'text-blue-900' :
                    contractorOnboardingStatus.onboarding_status === 'submitted' ? 'text-amber-900' :
                    contractorOnboardingStatus.onboarding_status === 'rejected' ? 'text-red-900' :
                    'text-green-900'
                  }`}>
                    {contractorOnboardingStatus.onboarding_status === 'draft' && 'Continue Contractor Setup'}
                    {contractorOnboardingStatus.onboarding_status === 'submitted' && 'Contractor Profile Under Review'}
                    {contractorOnboardingStatus.onboarding_status === 'rejected' && 'Contractor Profile Needs Updates'}
                    {contractorOnboardingStatus.onboarding_status === 'approved' && 'Contractor Profile Approved'}
                  </p>
                  <p className={`text-sm ${
                    contractorOnboardingStatus.onboarding_status === 'draft' ? 'text-blue-700' :
                    contractorOnboardingStatus.onboarding_status === 'submitted' ? 'text-amber-700' :
                    contractorOnboardingStatus.onboarding_status === 'rejected' ? 'text-red-700' :
                    'text-green-700'
                  }`}>
                    {contractorOnboardingStatus.onboarding_status === 'draft' && `Step ${contractorOnboardingStatus.onboarding_step} of 3 completed`}
                    {contractorOnboardingStatus.onboarding_status === 'submitted' && 'We\'re reviewing your application'}
                    {contractorOnboardingStatus.onboarding_status === 'rejected' && 'Please review and resubmit'}
                    {contractorOnboardingStatus.onboarding_status === 'approved' && 'You can now access contractor features'}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleStartContractorOnboarding}
                variant={contractorOnboardingStatus.onboarding_status === 'approved' ? 'default' : 'outline'}
                className={
                  contractorOnboardingStatus.onboarding_status === 'draft' ? 'border-blue-300 text-blue-700 hover:bg-blue-100' :
                  contractorOnboardingStatus.onboarding_status === 'submitted' ? 'border-amber-300 text-amber-700 hover:bg-amber-100' :
                  contractorOnboardingStatus.onboarding_status === 'rejected' ? 'border-red-300 text-red-700 hover:bg-red-100' :
                  'bg-green-600 hover:bg-green-700 text-white'
                }
                data-testid="continue-contractor-setup-btn"
              >
                {contractorOnboardingStatus.onboarding_status === 'draft' && (
                  <>Continue Setup <ArrowRight className="w-4 h-4 ml-1" /></>
                )}
                {contractorOnboardingStatus.onboarding_status === 'submitted' && 'View Status'}
                {contractorOnboardingStatus.onboarding_status === 'rejected' && 'Fix & Resubmit'}
                {contractorOnboardingStatus.onboarding_status === 'approved' && (
                  <>Go to Contractor Dashboard <ArrowRight className="w-4 h-4 ml-1" /></>
                )}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Welcome & Create Job CTA */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-zinc-500 mt-1">
              {activeJobs.length > 0 
                ? `You have ${activeJobs.length} active job${activeJobs.length > 1 ? 's' : ''}`
                : 'Ready to request a maintenance service?'}
            </p>
          </div>
          <Button
            data-testid="create-job-btn"
            onClick={() => navigate('/create-job')}
            className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg px-6 py-3"
          >
            <Plus className="w-4 h-4 mr-2" />
            Request a Zolt
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <p className="text-sm text-zinc-500">Active Jobs</p>
            <p className="text-2xl font-bold text-zinc-900">{activeJobs.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <p className="text-sm text-zinc-500">Completed</p>
            <p className="text-2xl font-bold text-zinc-900">{pastJobs.filter(j => j.status === 'completed').length}</p>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <p className="text-sm text-zinc-500">Total Spent</p>
            <p className="text-2xl font-bold text-zinc-900">
              £{pastJobs.filter(j => j.status === 'completed').reduce((sum, j) => sum + (j.estimated_price || 0), 0).toFixed(0)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 p-4 cursor-pointer hover:border-zinc-300" onClick={() => navigate('/messages')}>
            <p className="text-sm text-zinc-500">Messages</p>
            <p className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              View
            </p>
          </div>
        </div>

        {/* Active Jobs */}
        {activeJobs.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 mb-4">Active Jobs</h2>
            <div className="space-y-4">
              {activeJobs.map((job) => {
                const statusConfig = getStatusConfig(job.status);
                const StatusIcon = statusConfig.icon;
                
                return (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl border border-zinc-200 overflow-hidden hover:border-zinc-300 transition-all"
                  >
                    {/* Status Banner */}
                    <div className={`px-4 py-2 ${statusConfig.bg} border-b border-zinc-100`}>
                      <div className="flex items-center gap-2">
                        <StatusIcon className={`w-4 h-4 ${statusConfig.color} ${statusConfig.animated ? 'animate-pulse' : ''}`} />
                        <span className={`font-medium text-sm ${statusConfig.color}`}>
                          {statusConfig.text}
                        </span>
                        <span className="text-zinc-500 text-sm">
                          — {statusConfig.description}
                        </span>
                      </div>
                    </div>

                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-zinc-900">{job.category}</h3>
                          <p className="text-sm text-zinc-500">{job.subcategory}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-zinc-900">
                            {getCurrencySymbol(job.currency)}{job.estimated_price.toFixed(2)}
                          </p>
                        </div>
                      </div>

                      <p className="text-sm text-zinc-600 mb-3 line-clamp-2">{job.description}</p>

                      {/* Contractor Info (if assigned) */}
                      {job.contractor && (
                        <div className="bg-green-50 border border-green-100 rounded-lg p-3 mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-zinc-900">{job.contractor.name}</p>
                              <div className="flex items-center gap-2 text-xs text-zinc-600">
                                <Star className="w-3 h-3 text-zinc-900 fill-zinc-900" />
                                <span>{job.contractor.rating?.toFixed(1) || '5.0'}</span>
                                <span>•</span>
                                <span>{job.contractor.jobs_completed || 0} jobs</span>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/messages/job/${job.id}`)}
                              className="text-zinc-600"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Location */}
                      <div className="flex items-center gap-2 text-sm text-zinc-500 mb-3">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate">{job.address_text}</span>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => navigate(`/jobs/${job.id}`)}
                          className="flex-1 bg-zinc-900 hover:bg-zinc-800"
                          data-testid={`view-job-${job.id}`}
                        >
                          View Details
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                        {['requested', 'searching', 'offered'].includes(job.status) && (
                          <Button
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => openCancelDialog(job)}
                            data-testid={`cancel-job-${job.id}`}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {activeJobs.length === 0 && pastJobs.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-zinc-400" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">No jobs yet</h3>
            <p className="text-zinc-500 mb-6">Request your first Zolt to get started</p>
            <Button
              onClick={() => navigate('/create-job')}
              className="bg-zinc-900 hover:bg-zinc-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Request a Zolt
            </Button>
          </motion.div>
        )}

        {/* Past Jobs */}
        {pastJobs.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-zinc-900">Recent Jobs</h2>
              <Button
                variant="ghost"
                onClick={() => navigate('/jobs')}
                className="text-zinc-600"
              >
                View all
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="space-y-3">
              {pastJobs.slice(0, 5).map((job) => {
                const statusConfig = getStatusConfig(job.status);
                const StatusIcon = statusConfig.icon;
                
                return (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-white rounded-xl border border-zinc-200 p-4 hover:border-zinc-300 transition-all cursor-pointer"
                    onClick={() => navigate(`/jobs/${job.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${statusConfig.bg}`}>
                          <StatusIcon className={`w-5 h-5 ${statusConfig.color}`} />
                        </div>
                        <div>
                          <p className="font-medium text-zinc-900">{job.category}</p>
                          <p className="text-sm text-zinc-500">{job.subcategory}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-zinc-900">
                          {getCurrencySymbol(job.currency)}{job.estimated_price.toFixed(2)}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {new Date(job.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );

};

export default CustomerDashboard;
